import { Dispatch, SetStateAction, useState } from 'react';
import { formatPercentages, isNumeric, validateErrors } from '../../../../helpers';
import Button from '../../../../components/Button';
import TextInput from '../../../../components/TextInput';
import { config } from '../../../../config/config';
import {
    SLIPPAGE_CONFIGURED,
    SLIPPAGE_DEFAULT_CONFIGURED,
    Validators,
} from '../../../../config/constants';
import { parseEther } from 'ethers/lib/utils';
import useToastState from '../../../../context/toast/useToastState';
import useUserSlippage from '../../../../hooks/web3/useUserSlippage';

type SlippageSettingsProps = {
    setRenderSlippage: Dispatch<SetStateAction<boolean>>;
};

const SlippageSettings: React.FC<SlippageSettingsProps> = ({ setRenderSlippage }) => {
    const [slippage, setSlippage] = useState(localStorage.getItem('slippage') ?? '');

    const { setToast } = useToastState();

    const userSlippage = useUserSlippage();

    const validators: Validators[] = [
        {
            errorCondition:
                !slippage ||
                (isNumeric(slippage) &&
                    parseEther(slippage).lt(parseEther(config.slippage.min.toString()))),
            message: `Minimum slippage required is ${config.slippage.min}`,
        },
        {
            errorCondition:
                !slippage ||
                (isNumeric(slippage) &&
                    parseEther(slippage).gt(parseEther(config.slippage.max.toString()))),
            message: `Maximum slippage is ${config.slippage.max}`,
        },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setSlippage(value);
    };

    const handleAuto = () => {
        if (!slippage) return;

        setSlippage('');
        localStorage.removeItem('slippage');

        setToast(
            {
                type: 'primary',
                message: SLIPPAGE_DEFAULT_CONFIGURED,
            },
            SLIPPAGE_DEFAULT_CONFIGURED
        );
    };

    const onConfirm = () => {
        if (
            !validateErrors(validators, (message) =>
                setToast(
                    {
                        type: 'danger',
                        message,
                    },
                    message
                )
            )
        )
            return;

        localStorage.setItem('slippage', slippage);

        setToast(
            {
                type: 'primary',
                message: SLIPPAGE_CONFIGURED,
            },
            SLIPPAGE_CONFIGURED
        );
    };

    return (
        <div className='slippage-container'>
            <div className='label'>
                <span>Slippage tolerance</span>
                <span>Current: {formatPercentages(userSlippage)}%</span>
            </div>
            <div className='input-container'>
                <Button variant={slippage ? 'secondary' : 'primary'} onClick={handleAuto}>
                    Auto
                </Button>
                <TextInput
                    value={slippage}
                    placeholder={config.slippage.default.toString()}
                    onChange={handleChange}
                    name='slippage'
                />
                <span>%</span>
            </div>
            <div className='buttons-container'>
                <Button disabled={!slippage} onClick={onConfirm}>
                    Set Slippage
                </Button>
                <Button variant='secondary' onClick={() => setRenderSlippage(false)}>
                    Cancel
                </Button>
            </div>
        </div>
    );
};

export default SlippageSettings;
